import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ViewChild,
} from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Datastore } from '@freelancer/datastore';
import type {
  AiConsultantAudio,
  AiConsultantAudioCollection,
} from '@freelancer/datastore/collections';
import { DatastoreAiConsultantAudioModule } from '@freelancer/datastore/collections';
import { TrackingModule } from '@freelancer/tracking';
import { UiModule } from '@freelancer/ui';
import { HrColor } from '@freelancer/ui/hr';
import { IconColor, IconSize } from '@freelancer/ui/icon';
import { FontColor, FontWeight, TextSize } from '@freelancer/ui/text';
import { ToastAlertService, ToastAlertType } from '@freelancer/ui/toast-alert';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { firstValueFrom, type Observable } from 'rxjs';
import { AiConsultantThreadComponent } from './ai-consultant-thread/ai-consultant-thread.component';
import { AiConsultantToggleComponent } from './ai-consultant-toggle/ai-consultant-toggle.component';
import { AiConsultantVisualizerComponent } from './ai-consultant-visualizer/ai-consultant-visualizer.component';
import { SaveConversationToastError } from './ai-consultant.types';

@UntilDestroy({ className: 'AiConsultantComponent' })
@Component({
  standalone: true,
  imports: [
    AiConsultantThreadComponent,
    AiConsultantToggleComponent,
    AiConsultantVisualizerComponent,
    CommonModule,
    DatastoreAiConsultantAudioModule,
    MatTooltipModule,
    TrackingModule,
    UiModule,
  ],
  selector: 'app-ai-consultant',
  template: `
    <div
      class="Backdrop"
      flTrackingSection="AiConsultantWidget"
      flTrackingLabel="AiConsultantWidgetOverlay"
      *ngIf="isWidgetOpen"
      [attr.tabindex]="0"
      (click)="toggleWidget()"
    ></div>
    <div
      class="WidgetContainer"
      [flHide]="!isWidgetOpen"
      [attr.data-is-expanded]="isWidgetExpanded"
    >
      <div
        class="WidgetContainer-buttons"
        flTrackingSection="AiConsultantWidget"
        [flMarginBottom]="'xsmall'"
        [flMarginBottomTablet]="'none'"
      >
        <fl-icon
          class="SaveIcon"
          [flTrackingLabel]="'AiConsultantWidgetSaveConversationIcon'"
          matTooltip="Save this conversation"
          i18n-matTooltip="Icon tooltip for saving conversation"
          i18n-label="Save this conversation icon label"
          label="Save this conversation"
          [matTooltipPosition]="'below'"
          [color]="IconColor.LIGHT"
          [name]="'ui-floppy'"
          [size]="IconSize.SMALL"
          [clickable]="true"
          [hoverAnimation]="'none'"
          [clickAnimation]="'none'"
          (click)="handleSaveConversation()"
        ></fl-icon>
        <fl-icon
          *ngIf="isWidgetExpanded"
          [flShowDesktop]="true"
          [flTrackingLabel]="'AiConsultantWidgetExpand'"
          [color]="IconColor.LIGHT"
          [name]="'ui-arrows-contract'"
          [size]="IconSize.SMALL"
          [clickable]="true"
          [hoverAnimation]="'spin-and-fade'"
          [clickAnimation]="'none'"
          (click)="handleExpand(false)"
        ></fl-icon>
        <fl-icon
          *ngIf="!isWidgetExpanded"
          [flShowDesktop]="true"
          [flTrackingLabel]="'AiConsultantWidgetMinimize'"
          [color]="IconColor.LIGHT"
          [name]="'ui-arrows-expand'"
          [size]="IconSize.SMALL"
          [clickable]="true"
          [hoverAnimation]="'spin-and-fade'"
          [clickAnimation]="'none'"
          (click)="handleExpand(true)"
        ></fl-icon>
        <fl-icon
          [flTrackingLabel]="'AiConsultantWidgetToggleView'"
          [color]="IconColor.LIGHT"
          [name]="'ui-close'"
          [size]="IconSize.SMALL"
          [label]="'Close AI Consultant'"
          [clickable]="true"
          [hoverAnimation]="'spin-and-fade'"
          [clickAnimation]="'none'"
          (click)="toggleWidget()"
        ></fl-icon>
      </div>
      <div>
        <fl-text
          i18n="Ai chat thread header title"
          [color]="FontColor.LIGHT"
          [weight]="FontWeight.BOLD"
          [flMarginBottom]="'xsmall'"
          [size]="TextSize.SMALL"
        >
          Chat with Ava - Your AI Business Consultant
        </fl-text>
        <fl-hr
          [color]="HrColor.LIGHT"
          [flMarginBottom]="'xsmall'"
        ></fl-hr>
      </div>
      <div class="VisualizerThread">
        <app-ai-consultant-visualizer
          *ngIf="isWidgetOpen"
          class="WidgetContainer-visualizer"
          [isPlaying]="isPlaying"
          [src]="
            playingMessage
              ? (audioPerMessage.get(playingMessage) | flAsync)?.audio
              : undefined
          "
          [flShowDesktop]="true"
          (onAudioEnded)="handleAudioEnded()"
        ></app-ai-consultant-visualizer>
        <app-ai-consultant-thread
          #aiConsultantThread
          class="WidgetContainer-thread"
          [playingMessage]="playingMessage"
          [playingMessageSrc]="
            playingMessage
              ? (audioPerMessage.get(playingMessage) | flAsync)?.audio
              : undefined
          "
          [isPlaying]="isPlaying"
          (openErrorToast)="handleOpenErrorToast($event)"
          (playAudio)="handlePlayAudio($event)"
          (pauseAudio)="handlePauseAudio()"
        ></app-ai-consultant-thread>
      </div>
    </div>
    <app-ai-consultant-toggle
      #aiConsultantToggle
      class="AiConsultantToggle"
      [flHide]="isWidgetOpen"
      (onClick)="toggleWidget()"
    >
    </app-ai-consultant-toggle>

    <fl-toast-alert
      i18n="Generic error message"
      [id]="SaveConversationToastError.GENERIC_ERROR"
      [type]="ToastAlertType.ERROR"
    >
      Something went wrong while sending the conversation to your email. Please
      try again later.
    </fl-toast-alert>
    <fl-toast-alert
      i18n="Rate limit error message"
      [id]="SaveConversationToastError.RATE_LIMIT_ERROR"
      [type]="ToastAlertType.ERROR"
    >
      You can only save your conversation once per hour. Please try again later.
    </fl-toast-alert>
    <fl-toast-alert
      i18n="Short chat history error message"
      [id]="SaveConversationToastError.SHORT_HISTORY_ERROR"
      [type]="ToastAlertType.ERROR"
    >
      Your conversation is too short. Keep chatting with Ava to enable saving.
    </fl-toast-alert>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./ai-consultant.component.scss'],
})
export class AiConsultantComponent {
  FontColor = FontColor;
  FontWeight = FontWeight;
  HrColor = HrColor;
  IconColor = IconColor;
  IconSize = IconSize;
  SaveConversationToastError = SaveConversationToastError;
  ToastAlertType = ToastAlertType;
  TextSize = TextSize;

  isWidgetExpanded = false;
  isWidgetOpen = false;

  @ViewChild('aiConsultantThread')
  aiConsultantThread: AiConsultantThreadComponent;

  @ViewChild('aiConsultantToggle')
  aiConsultantToggle: AiConsultantToggleComponent;

  audioPerMessage = new Map<string, Observable<AiConsultantAudio>>();
  playingMessage?: string;
  isPlaying = false;

  constructor(
    private cd: ChangeDetectorRef,
    private datastore: Datastore,
    private toastAlertService: ToastAlertService,
  ) {}

  toggleWidget(): void {
    this.isWidgetOpen = !this.isWidgetOpen;
    this.handleAudioEnded();
    this.cd.markForCheck();
    this.aiConsultantThread.scrollableContent?.scrollToBottom();
  }

  handleExpand(isExpanded: boolean): void {
    this.isWidgetExpanded = isExpanded;
    this.aiConsultantThread.scrollableContent?.scrollToBottom();
  }

  handlePauseAudio(): void {
    this.isPlaying = false;
  }

  handlePlayAudio(message: string): void {
    this.playingMessage = message;
    this.isPlaying = false;

    const audio$ = this.storeSrcObservable(message);
    firstValueFrom(audio$.pipe(untilDestroyed(this))).then(() => {
      this.isPlaying = true;
    });

    this.cd.markForCheck();
  }

  handleSaveConversation(): void {
    this.aiConsultantThread.sendChatTranscript();
  }

  handleOpenErrorToast(errorToastId: SaveConversationToastError): void {
    this.toastAlertService.open(errorToastId);
  }

  storeSrcObservable(message: string): Observable<AiConsultantAudio> {
    const audio$ = this.audioPerMessage.get(message);
    if (!audio$) {
      this.datastore
        .collection<AiConsultantAudioCollection>('aiConsultantAudio')
        .push({ input: message });
      const newAudio$ = this.datastore
        .document<AiConsultantAudioCollection>('aiConsultantAudio', message)
        .valueChanges();
      this.audioPerMessage.set(message, newAudio$);
      return newAudio$;
    }
    return audio$;
  }

  handleAudioEnded(): void {
    this.playingMessage = undefined;
    this.isPlaying = false;
  }

  playToggleVideo(): void {
    this.aiConsultantToggle.playVideo();
  }

  openToggleCallout(): void {
    this.aiConsultantToggle.openCallout();
  }
}
